import { useEffect, useState } from "react";
import {
  Create,
  SimpleForm,
  FormDataConsumer,
  useNotify,
  useLoading,
  useRedirect,
  TextInput,
  required,
  email,
} from "react-admin";
import { Typography, Grid, CircularProgress } from "@mui/material";
import { useLocation } from "react-router-dom";
import { settings } from "app";
import { tenants } from "utils/tenants";
import dataProvider from "server/dataProvider";
import {
  CandidatePaymentStatus,
  CandidatePaymentStatusTitles,
  EDUCATION_STUDENTS,
  EnrollmentFormType,
  STUDENTS_PATH,
  CitizenshipIds,
} from "utils/constants";

import PreferenceSection from "../../Candidates/Create/Partials/PreferenceSection";
import { ActiveEditToolbar } from "../Partials/ActiveSave";
import SearchInput from "../Partials/SearchInput";
import PersonalInfo from "../Partials/PersonalInfoSection";
import NamesSectionEn from "../Partials/NamesEnSection";
import AddressSection from "../Partials/AddressSection";
import AcademicInformationSection from "../Partials/AcademicInformationSection";
import HealthInsuranceSection from "../Partials/HealthInsuranceSection";
import BackButtonToolbar from "features/BackButtonToolbar";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import { SectionTitle } from "features/SectionTitle";
import { formatDateOnlyBg } from "server/formatting/formatDateBg";
import { getStyle } from "customStyles/utils";
import { candidateRequests } from "api/candidateApi";
import { studentRequests } from "api/studentApi";
import { accountRequests } from "api/accountApi";
import { administrationRequests } from "api/administrationApi";
import { useNotifyError } from "utils/useNotifyError";

const CreateEnrollmentForm = ({ props, resource, title, isIndividual, isOther, isPms, isKSK }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const location = useLocation();
  const loading = useLoading();
  const redirect = useRedirect();
  const style = getStyle(settings.tenant);
  const isMgu = settings.tenant === tenants.MGU;
  //states

  const [pmsType, setPmsType] = useState(null);
  const [loadingSend, setLoadingSend] = useState(false);
  const [isDisable, setIsDisable] = useState(false);
  const [userData, setData] = useState(null);
  const [same, setSame] = useState(false);
  const [profilePictureDocumentId, setProfilePictureDocumentId] = useState(null);
  const [personalDocumentFrontId, setPersonalDocumentFrontId] = useState(null);
  const [personalDocumentBackId, setPersonalDocumentBackId] = useState(null);
  const [diplomaIds, setDiplomaIds] = useState([]);
  const [providedSpecialGroup, setProvidedSpecialGroup] = useState(null);
  const [defaultBirthCountry, setBirthCountry] = useState(21);

  const [languageId, setLanguageId] = useState(null);

  const [curriculumId, setCurriculumId] = useState(null);
  const [searchValue, setSearchValue] = useState(null);
  const [specialityId, setSpecialityId] = useState(userData?.specialityId || location?.state?.specialityId || null);
  // const [paidOnlineData, setPaidOnlineData] = useState(null);
  const candidacyId = location?.state?.candidacyId;
  const [citizenshipId, setCitizenshipId] = useState(null); 
  const [foreignerType, setForeignerType] = useState(null);
  const [personIdType, setPersonIdType] = useState(null);
  const [isNotPaidOnline, setIsPaidNotOnline] = useState(false);
  const [campaignRankingInfoString, setCampaignRankingInfoString] = useState(null);
  const [availableGrades, setAvailableGrades] = useState(null);
  const isForeignerPath = location.pathname.includes("/en/");
  const [isForeigner, setIsForeigner] = useState(isForeignerPath);
  // Forms that start with a user lookup (by ЕГН or, for foreigners, by email) instead of a pre-loaded candidacy.
  const hasSearch = isIndividual || isOther || isPms;

  const [discount, setDiscount] = useState({});
  const [isFreeEnrolling, setIsFreeEnrolling] = useState(false);

  useEffect(() => {
    if (Object.values(discount).some(x => x.discounts.Semester === true)) {
      setIsFreeEnrolling(true);
    } else {
      setIsFreeEnrolling(false);
    }
  }, [discount]);

  const [state, setState] = useState({
    HasEmploymentIncome: false,
    ReceivesSurvivorPension: false,
    HasOtherTaxableHealthIncome: false,
  });
  const [notSubjectToInsurance, setNotSubjectToInsurance] = useState(false);

  useEffect(() => {
    if (userData) {
      setState({
        HasEmploymentIncome: userData?.healthInsuranceParams?.includes("HasEmploymentIncome") || false,
        ReceivesSurvivorPension: userData?.healthInsuranceParams?.includes("ReceivesSurvivorPension") || false,
        HasOtherTaxableHealthIncome: userData?.healthInsuranceParams?.includes("HasOtherTaxableHealthIncome") || false,
      });
    }
  }, [userData]);

  const healthInsuranceParams = Object.keys(state).filter(key => state[key]);

  const candidatePaymentStatus = data => CandidatePaymentStatusTitles[data?.status];
  // hooks

  useEffect(() => {
    if (!isKSK) return;

    const loadAvailableGrades = async () => {
      try {
        const available = await dataProvider.customRequest(
          candidateRequests.getCandidacySchoolSubjectGrades(candidacyId),
        );
        if (!available.data) return;

        const allGrades = await dataProvider.customRequest(candidateRequests.getSchoolSubjectsSelect());
        const grades = allGrades.data
          .map(item => {
            const foundItem = available.data.find(x => x.schoolSubjectId === item.id);
            if (foundItem) {
              return {
                schoolSubjectId: item.id,
                nameBg: item.nameBg,
                grade: foundItem.grade,
              };
            }
            return null;
          })
          .filter(Boolean);

        setAvailableGrades(grades);
      } catch (e) {
        console.error("Error fetching available grades", e);
      }
    };

    const loadEnrollment = async () => {
      try {
        const res = await dataProvider.customRequest(
          candidateRequests.getCandidateEnrollments({ candidacyId, specialityId: location?.state?.specialityId }),
        );
        setData(res?.data);
        setCitizenshipId(res?.data?.userInfo?.citizenshipId);
        setIsForeigner(res?.data?.userInfo?.citizenshipId !== CitizenshipIds.Bulgarians || isForeignerPath);
        setCampaignRankingInfoString(
          <>
            Приет по заповед № <span style={{ color: "black" }}>{res?.data?.campaignRankingOrderNumber} </span>
            Дата: <span style={{ color: "black" }}>{formatDateOnlyBg(res?.data?.campaignRankingOrderDate)}</span>
          </>,
        );
        setLanguageId(res?.data?.instructionLanguageId);
        setIsPaidNotOnline(res?.data?.payment?.status === CandidatePaymentStatus.NotPaidOnline);
        
        setProfilePictureDocumentId(res?.data?.profilePictureDocumentId);
        setPersonalDocumentFrontId(res?.data?.personalDocument?.frontDocumentId);
        setPersonalDocumentBackId(res?.data?.personalDocument?.backDocumentId);
        setDiplomaIds(res?.data?.diploma?.documentIds)

        await loadAvailableGrades();
      } catch (e) {
        notifyError(e);
      }
    };

    loadEnrollment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (specialityId && !isOther) {
      dataProvider
        .customRequest(studentRequests.getCurriculumsCurrentSelect({ specialityId: specialityId }))
        .then(res => {
          setCurriculumId(res?.data?.curriculumId);
        })
        .catch(e => {
          setCurriculumId(null);
          notifyError(e);
        });
    }
    if (specialityId && !isKSK) {
      dataProvider
        .customRequest(administrationRequests.getSpecialityHasSpecialGroup(specialityId))
        .then(res => {
          setProvidedSpecialGroup(res?.data?.provides);
        })
        .catch(e => {
          notifyError(e);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialityId]);

  //functionalities
  const searchUser = async () => {
    if (!searchValue) {
      notify(isForeigner ? "Моля въведете имейл!" : "Моля въведете ЕГН!", { type: "error" });
    } else if (isForeigner ? email()(searchValue) : !searchValue.match(/^[0-9]{10}$/)) {
      notify(isForeigner ? "Моля въведете валиден имейл!" : "Моля въведете валидно ЕГН!", { type: "error" });
    } else {
      setData(null);
      const searchParams = isForeigner ? { email: searchValue } : { personId: searchValue };
      try {
        const { data } = await dataProvider.customRequest(
          accountRequests.getUsersInfoSearch(searchParams),
          null,
          "GET",
        );

        if (Object.entries(data).length > 0) {
          const { citizenship, lastName, firstName, secondName, ...rest } = data;
          setData(prevData => ({
            ...prevData,
            userInfo: {
              ...(prevData?.userInfo || {}),
              ...rest,
              citizenshipId: citizenship,
              firstNameBg: firstName,
              secondNameBg: secondName,
              lastNameBg: lastName,
            },
          }));
        }
      } catch (error) {
        notifyError(error);
        // Not found → still let the operator fill the form manually.
        setData(hasSearch ? [] : null);
      }
    }
  };
  const handleChange = event => {
    if (!isDisable && !loading) {
      setState({
        ...state,
        [event.target.name]: event.target.checked,
      });
    }
  };

  const convertData = (obj, keysToRemove = [], prefixesToRemove = []) => {
    const removedData = Object.fromEntries(
      Object.entries(obj)
        .filter(([key]) => !keysToRemove.includes(key) && !prefixesToRemove.some(prefix => key.startsWith(prefix)))
        .map(([key, value]) => {
          const dateKeys = ["visadIssuedOn", "birthDate", "diploma", "personalDocument"];
          const nestedDateKeys = ["graduatedOn", "issuedOn", "validUntil"];

          if (typeof value === "string" && dateKeys.includes(key)) {
            value = value.split("T")[0];
          }
          if (typeof value === "object" && value !== null && dateKeys.includes(key)) {
            value = Object.fromEntries(
              Object.entries(value).map(([k, val]) => {
                if (typeof val === "string" && nestedDateKeys.includes(k)) {
                  return [k, val.split("T")[0]];
                }
                return [k, val];
              }),
            );
          }
          return [key, value];
        }),
    );

    return removedData;
  };

  const handleSend = data => {
    setLoadingSend(true);
    // Default to Bulgarian (id 1) only when there's truly no other source for citizenship:
    // no value entered on the form, no value on the fetched/found user record, and we're not on a foreigner route.
    const resolvedCitizenshipId =
      data?.userInfo?.citizenshipId || userData?.userInfo?.citizenshipId || (!isForeigner ? CitizenshipIds.Bulgarians : undefined);
    if (isKSK) {
      return {
        ...convertData(
          data,
          ["profilePictureDocumentId", "backDocumentId", "frontDocumentId", "documentIds"],
          ["backDocumentId_", "documentIds_", "frontDocumentId_", "profilePictureDocumentId_"],
        ),
        campaignRankingOrderDate: userData.campaignRankingOrderDate || data.campaignRankingOrderDate,
        campaignRankingOrderNumber: userData.campaignRankingOrderNumber || data.campaignRankingOrderNumber,
        candidacyId,
        userInfo: {
          ...data.userInfo,
          citizenshipId: resolvedCitizenshipId,
          // Empty name inputs now keep "" (see keepEmptyString in the name sections) - send null as before.
          firstNameEn: data?.userInfo?.firstNameEn || null,
          secondNameEn: data?.userInfo?.secondNameEn || null,
          lastNameEn: data?.userInfo?.lastNameEn || null,
          secondNameBg: data?.userInfo?.secondNameBg || null,
          contactPhone: data?.userInfo?.contactPhone || "0000000000",
        },
        personalDocument: {
          ...convertData(data).personalDocument,
          frontDocumentId: personalDocumentFrontId,
          backDocumentId: personalDocumentBackId, // optional
        },
        profilePictureDocumentId: profilePictureDocumentId,
        healthInsuranceParams: healthInsuranceParams,
        notSubjectToInsurance: notSubjectToInsurance,
        diploma: {
          ...convertData(data).diploma,
          documentIds: diplomaIds,
        },
        otherUniversityInfo: isOther ? data?.otherUniversityInfo : null,
      };
    } else {
      return {
        ...convertData(
          data,
          ["profilePictureDocumentId", "backDocumentId", "frontDocumentId", "documentIds"],
          ["backDocumentId_", "documentIds_", "frontDocumentId_", "profilePictureDocumentId_"],
        ),
        profilePictureDocumentId: profilePictureDocumentId,
        userId: userData?.userInfo?.userId || null,
        userInfo: {
          ...data.userInfo,
          citizenshipId: resolvedCitizenshipId,
          // Empty name inputs now keep "" (see keepEmptyString in the name sections) - send null as before.
          firstNameEn: data?.userInfo?.firstNameEn || null,
          secondNameEn: data?.userInfo?.secondNameEn || null,
          lastNameEn: data?.userInfo?.lastNameEn || null,
          secondNameBg: data?.userInfo?.secondNameBg || null,
          contactPhone: data?.userInfo?.contactPhone || "0000000000",
        },
        otherUniversityInfo: isOther ? data?.otherUniversityInfo : null,
        personalDocument: {
          ...convertData(data).personalDocument,
          frontDocumentId: personalDocumentFrontId,
          backDocumentId: personalDocumentBackId, // optional
        },
        diploma: {
          ...convertData(data).diploma,
          documentIds: diplomaIds,
        },
        healthInsuranceParams: healthInsuranceParams,
        notSubjectToInsurance: notSubjectToInsurance,
      };
    }
  };

  //return parent component

  return (
    <div style={loadingSend ? { opacity: 0.5 } : { opacity: 1 }}>
      {loadingSend && (
        <div style={style.loadingStyle.container}>
          <CircularProgress size={200} />
          <div style={style.loadingStyle.text}>Saving...</div>
        </div>
      )}
      {hasSearch && (
        <SearchInput
          searchValue={searchValue || ""}
          setSearchValue={setSearchValue}
          setCurriculumId={setCurriculumId}
          setData={setData}
          searchUser={searchUser}
          isForeigner={isForeigner}
        />
      )}
      {userData && (
        <Create
          {...props}
          title={
            isKSK
              ? title +
                `${
                  citizenshipId === CitizenshipIds.Bulgarians
                    ? " - Българи ЕС"
                    : citizenshipId === CitizenshipIds.EuForeigners
                      ? " - Чужденци ЕС"
                      : " - Чужденци извън ЕС"
                }`
              : settings.tenant === tenants.MGU
                ? title.replace(" - Българи", "")
                : title
          }
          transform={handleSend}
          resource={
            isKSK ? `candidate/candidate-enrollments?formType=${EnrollmentFormType.AcceptedCandidate}` : resource
          }
          mutationOptions={{
            onSuccess: data => {
              setLoadingSend(false);
              if (data?.props?.facultyNumber !== undefined) {
                redirect(
                  `/${STUDENTS_PATH}/${EDUCATION_STUDENTS}?filter={${JSON.stringify("facultyNumber")}:${JSON.stringify(
                    data.props.facultyNumber,
                  )},${JSON.stringify("specialityId")}:${JSON.stringify(specialityId)}}`,
                );
              } else {
                redirect(`/${STUDENTS_PATH}/${EDUCATION_STUDENTS}`);
              }
            },
            onError: error => {
              console.error(error);

              setLoadingSend(false);
              notifyError(error);
            },
          }}
          actions={<BackButtonToolbar />}
        >
          <SimpleForm
            toolbar={
              <ActiveEditToolbar
                loading={loading || loadingSend}
                isDisable={isDisable}
              />
            }
            sx={{ margin: "2rem", paddingX: "2rem", borderLeft: "5px solid #4E2683" }}
          >
            {isKSK ? (
              <>
                <Typography
                  border={"1px solid green"}
                  borderRadius={5}
                  color={"green"}
                  sx={{ marginLeft: "auto" }}
                  fontSize={18}
                  paddingY={1}
                  paddingX={2}
                  marginBottom={2}
                >
                  {campaignRankingInfoString}
                </Typography>
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                  <SectionTitle paddingY={2} title="Лични данни" />

                  <Typography
                    border={
                      userData?.payment?.status === CandidatePaymentStatus.NotPaidOnline
                        ? "1px solid darkRed"
                        : "1px solid green"
                    }
                    borderRadius={5}
                    color={userData?.payment?.status === CandidatePaymentStatus.NotPaidOnline ? "darkRed" : "green"}
                    fontWeight={"bold"}
                    fontSize={18}
                    paddingY={1}
                    paddingX={2}
                    marginBottom={2}
                  >
                    {candidatePaymentStatus(userData?.payment) || "Не е платено онлайн"}
                  </Typography>
                </div>
              </>
            ) : (
              <SectionTitle paddingY={2} title="Лични данни" />
            )}
            {/* // Лични данни */} {/* //PersonalInfo */}
            <PersonalInfo
              isDisable={isDisable}
              userData={userData}
              isIndividual={isIndividual}
              isOther={isOther}
              isKSK={isKSK}
              isPms={isPms}
              notify={notify}
              isForeigner={isForeigner}
              defaultBirthCountry={defaultBirthCountry}
              setPersonalDocumentFrontId={setPersonalDocumentFrontId}
              setPersonalDocumentBackId={setPersonalDocumentBackId}
              setIsDisable={setIsDisable}
              setForeignerType={setForeignerType}
              setPersonIdType={setPersonIdType}
              personIdType={personIdType}
              setPmsType={setPmsType}
            />
            {/* // Информация за кампанията */} {/* //CampaignInformation */}
            <SectionTitle paddingY={2} title="Информация за кампанията" />
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextInput
                  label="Заповед номер"
                  source="campaignRankingOrderNumber"
                  defaultValue={userData?.campaignRankingOrderNumber ?? (isMgu ? "РД-12-114" : null)}
                  readOnly={userData?.campaignRankingOrderDate !== undefined}
                  validate={!isForeigner ? required() : null}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <CustomDateInput
                  source="campaignRankingOrderDate"
                  label="Дата"
                  defaultValue={userData?.campaignRankingOrderDate ?? (isMgu ? "2026-07-07T00:00:00" : null)}
                  validate={!isForeigner ? required("Задължително") : null}
                  readOnly={userData?.campaignRankingOrderDate !== undefined}
                  required={!isMgu && !isForeigner}
                />
              </Grid>
            </Grid>
            {/* Preference Section */}
            {!isForeigner && !isPms ? (
              <Grid
                container
                spacing={2}
              >
                <Grid
                  item
                  xs={12}
                >
                  <FormDataConsumer>
                    {({ formData, ...rest }) => {
                      return (
                        <PreferenceSection
                          discount={discount}
                          setDiscount={setDiscount}
                          formData={formData}
                          defaultValue={
                            userData?.preferenceIds?.length > 0 ? userData?.preferenceIds.map(x => ({ id: x })) : [{}]
                          }
                          isDisable={isDisable}
                        />
                      );
                    }}
                  </FormDataConsumer>
                </Grid>
              </Grid>
            ) : null}
            <Typography
              color={"primary"}
              fontSize={18}
              paddingY={2}
            >
              Имена на латиница
            </Typography>
            {/* Имена на латиница same for both forms */}{" "}
            <NamesSectionEn
              isDisable={isDisable}
              userData={userData}
              isForeigner={isForeigner}
            />
            {/* Място на раждане и адреси */}
            <AddressSection
              defaultBirthCountry={defaultBirthCountry}
              setBirthCountry={setBirthCountry}
              userData={userData}
              same={same}
              setSame={setSame}
              isDisable={isDisable}
              isForeigner={isForeigner}
            />
            {/* Данни за диплома */}
            <AcademicInformationSection
              isFreeEnrolling={isFreeEnrolling}
              pmsType={pmsType}
              userData={userData}
              isDisable={isDisable}
              setIsDisable={setIsDisable}
              defaultBirthCountry={defaultBirthCountry}
              setSpecialityId={setSpecialityId}
              specialityId={isKSK ? location?.state?.specialityId : specialityId}
              setProfilePictureDocumentId={setProfilePictureDocumentId}
              setLanguageId={setLanguageId}
              notify={notify}
              isForeigner={isForeigner}
              setDiplomaIds={setDiplomaIds}
              curriculumId={curriculumId}
              setCurriculumId={setCurriculumId}
              languageId={languageId}
              isIndividual={isIndividual}
              isOther={isOther}
              isKSK={isKSK}
              isPms={isPms}
              providedSpecialGroup={providedSpecialGroup}
              isNotPaidOnline={isNotPaidOnline}
              availableGrades={availableGrades}
            />
            {/* //ЗО формуляри */}
            <HealthInsuranceSection
              citizenshipId={citizenshipId}
              state={state}
              handleChange={handleChange}
              isDisable={isDisable}
              isForeigner={isForeigner}
              foreignerType={foreignerType}
              specialityId={isKSK ? Number(location?.state?.specialityId) : specialityId}
              healthInsuranceParams={healthInsuranceParams}
              setNotSubjectToInsurance={setNotSubjectToInsurance}
              notSubjectToInsurance={notSubjectToInsurance}
              SectionTitle={SectionTitle}
              setState={setState}
              isPms={isPms}
              isKSK={isKSK}
              personIdType={personIdType}
              defaultBirthCountry={defaultBirthCountry}
            />
          </SimpleForm>
        </Create>
      )}
    </div>
  );
};
export default CreateEnrollmentForm;
